import { BreakpointObserver } from '@angular/cdk/layout';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isEqual, isNil, noop } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  FormGroupComponent,
  FormsUtil,
  GuidEmpty,
  InputComponent,
  ListSelectComponent,
  MbsSize,
  ModalService,
  ModalSettings,
  PrependTopControlsLabel,
  RadioComponent,
  SelectComponent,
  TableCellDirective,
  TableHeader,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { ItemTypeEnum, TreeBunchesIcons } from 'mbs-ui-shared@models/backup/storages-type';
import { minimalWindowsVersionForNewAsync } from 'mbs-ui-shared@models/Computer';
import { ErrorHandlerService } from 'mbs-ui-shared@services/error-handler.service';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { ClusterStepValue } from 'wizards/models/cluster-models';
import { SelectHostStepValue } from 'wizards/models/select-host-models';
import {
  SelectVirtualMachinesStepValue,
  VirtualMachine,
  VirtualMachinesSelectedType,
  VirtualMachinesSelectedWithNumbersType,
  VirtualMachinesType
} from 'wizards/models/select-virtual-machines-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  CheckLicenseResult,
  MachinesDisksLoadedStatuses,
  ParamsForCheckVMSocketLicenses,
  ParamsForGetVirtualMachinesOrDisksList,
  WizardStepsService
} from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

const AgentVersionWithNewCheckSocketLicense = 860;
const MachineRowHeight = 40;
const MachineListHeight = '60vh';
const MachinesGridColumns = [
  { query: '(min-width: 1920px)', columns: 4 },
  { query: '(min-width: 1440px)', columns: 3 },
  { query: '(min-width: 992px)', columns: 2 }
];
const MachinesGridMinColumns = 1;

enum MachineTypeFilter {
  All = 'All',
  Containers = 'Containers',
  Machines = 'Machines'
}

type MachineTypeFilterItem = { id: MachineTypeFilter; name: string };

/**
 * Component for "Select Virtual Machines" step
 * Displays a list of computers (virtual machines) available for selection
 * Used within the HyperV and VMWare wizards
 * @authors Sergey.P Roman.sh
 */
const SelectVirtualMachinesStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => SelectVirtualMachinesStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-select-virtual-machines-step',
  templateUrl: './select-virtual-machines-step.component.html',
  providers: [SelectVirtualMachinesStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    FormsModule,
    I18NextPipe,
    InputComponent,
    ListSelectComponent,
    NgbTooltipModule,
    RadioComponent,
    ReactiveFormsModule,
    SelectComponent,
    TextEllipsisComponent,
    TableCellDirective,
    PrependTopControlsLabel
  ]
})
export class SelectVirtualMachinesStepComponent extends StepBase<SelectVirtualMachinesStepValue> implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  mainService: RemoteManagementWizardsService;
  stepService = inject(WizardStepsService);
  private errorService = inject(ErrorHandlerService);
  private breakpointObserver = inject(BreakpointObserver);
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private document = inject<Document>(DOCUMENT);

  @Input() selectedHostCredentials: SelectHostStepValue;
  @Input() cluster: ClusterStepValue;

  @Output() machinesSelected: EventEmitter<Array<VirtualMachine>> = new EventEmitter<Array<VirtualMachine>>();

  @ViewChild('searchMachinesModal', { static: true, read: TemplateRef }) searchMachinesModal: TemplateRef<HTMLElement>;

  public readonly MbsSize = MbsSize;
  public readonly itemTypeEnum = ItemTypeEnum;
  public readonly fieldName = this.isProxmox ? 'displayName' : 'name';
  public readonly elementsSelector = {
    name: {
      availableSocketsErrorDetail: 'available-socket-detail-alert-error',
      vmListSelect: 'virtual-machines-list'
    },
    id: {
      allRadio: 'type-backup-all-1',
      runningRadio: 'type-backup-running-2',
      selectedRadio: 'type-backup-selected-3',
      searchButton: 'search-virtual-machines-button',
      searchInput: 'search-virtual-machines-input',
      searchTypeFilter: 'search-virtual-machines-type-filter',
      searchMachineCheckbox: 'search-virtual-machine-checkbox-',
      searchSelectAll: 'search-virtual-machines-select-all',
      virtualMachinesList: 'virtual-machines-list'
    }
  };

  public minusPixels = 180;

  public virtualMachinesSelectedType = VirtualMachinesSelectedType;
  public virtualMachinesIcon = TreeBunchesIcons.HyperV;
  public proxmoxContainerIcon = TreeBunchesIcons.ProxmoxContainer;

  public headers: TableHeader[] = [{ name: '', gridColSize: '1fr', overflow: true }];
  public machinesLoading = false;
  public loadingCheckHyperV = false;
  public machinesTotal = 0;
  public machinesLoadedTotal = 0;
  public notLoaded = 0;
  public notLoadedSelected = 0;
  public loadedMachines: VirtualMachine[] = [];
  public loadedMachinesHashTable: { [key: string]: VirtualMachine } = {};
  public selectedMachinesNames: string[] = [];
  public selectedMachines: VirtualMachine[] = [];
  public selectedMachineIds = new Set<string>();

  public readonly machineListHeight = MachineListHeight;
  public readonly machineRowHeight = MachineRowHeight;

  public searchQuery = '';
  public typeFilter = MachineTypeFilter.All;
  public typeFilterItems: MachineTypeFilterItem[] = [];
  public filteredMachines: VirtualMachine[] = [];
  public machineRows: VirtualMachine[][] = [];
  public columnsCount = 1;
  public allFilteredMachinesSelected = false;
  public someFilteredMachinesSelected = false;

  public invalidDisks = false;
  public hyperVNotInstalled = null;

  public availableSocketsErrorDetail = '';

  /** User must buy licence for backup of every virtual machine. */
  public hasAvailableSockets: boolean = undefined;

  public get virtualizationNotInstalled(): boolean {
    return this.isHyperV && !!this.hyperVNotInstalled;
  }

  public notRDAndNotOffline(): boolean {
    return !this.isRDMode && !this.isOffline;
  }

  private firstClusterData = null;
  private isFirstLoad = true;
  private needOldAsync = false;
  private needNewCheckSocketLicense = false;
  private lastValidCredentials: SelectHostStepValue;
  private availableSocketsRequest: { inProgress?: boolean; finished?: boolean; params?: ParamsForCheckVMSocketLicenses } = {};
  private cacheSelectedMachines: VirtualMachine[] = [];
  private pendingSearchModal = false;

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.calcMinusPixelsForCard();

    this.typeFilterItems = [
      { id: MachineTypeFilter.All, name: this.i18nPipe.transform('wizards:searchVirtualMachinesFilterAll') },
      { id: MachineTypeFilter.Containers, name: this.i18nPipe.transform('wizards:searchVirtualMachinesFilterContainers') },
      { id: MachineTypeFilter.Machines, name: this.i18nPipe.transform('wizards:searchVirtualMachinesFilterMachines') }
    ];

    this.observeGridColumnsCount();

    if (this.isVMWare) this.virtualMachinesIcon = TreeBunchesIcons.VmWare;
    if (this.isProxmox) this.virtualMachinesIcon = TreeBunchesIcons.Proxmox;

    const version = +this.mainService?.backupVersionUpdated?.substring(0, 3) || 0;

    this.needOldAsync = version < minimalWindowsVersionForNewAsync;
    this.needNewCheckSocketLicense = this.isProxmox && version >= AgentVersionWithNewCheckSocketLicense;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup(
      {
        type: new FormControl(VirtualMachinesSelectedType.All),
        machines: new FormControl([]),
        machinesFullFormat: new FormControl([])
      },
      this.machinesDuplicateValidator.bind(this)
    );

    this.mainService.machinesDisksStatuses$.pipe(untilDestroyed(this)).subscribe((statuses: MachinesDisksLoadedStatuses) => {
      if (statuses && this.stepForm) {
        queueMicrotask(() => {
          this.stepForm.updateValueAndValidity();
          this.calcMinusPixelsForCard();
          this.cdr.detectChanges();
        });
        this.getMachineWithValidatedDisks(statuses.voidMachines, statuses?.allVoid);
      }
    });

    this.initFormEvents();
  }

  onStepFormChange(value: SelectVirtualMachinesStepValue): void {
    this.value = {
      ...value,
      valid:
        this.stepForm.valid &&
        !this.virtualizationNotInstalled &&
        (!this.isEdit || !this.notLoadedSelected) &&
        (this.isRDMode ||
          this.isOffline ||
          ((value.type !== VirtualMachinesSelectedType.Selected || !!value.machines?.length) && !this.invalidDisks))
    };
  }

  machinesDuplicateValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
    const type = formGroup.get('type').value;
    const machines =
      type !== VirtualMachinesSelectedType.Selected
        ? this.loadedMachines?.map((m) => m[this.fieldName]) || []
        : formGroup.get('machines').value;

    return new Set(machines).size !== machines.length ? { duplicatesExist: true } : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((this.isVMWare || this.isProxmox) && this.needLoadMachines(changes)) {
      this.loadMachines();
      this.lastValidCredentials = this.extractCred(changes.selectedHostCredentials.currentValue);
    }

    if (this.isHyperV && this.needLoadMachinesIfCluster(changes)) {
      this.loadedMachines = [];
      this.loadMachines();
    }
  }

  private needLoadMachinesIfCluster(changes: SimpleChanges): boolean {
    const current = changes?.cluster?.currentValue;
    const prev = changes?.cluster?.previousValue;
    const firstOrLoading = this.isFirstLoad || this.loadingCheckHyperV || this.machinesLoading;

    if (this.isEdit && (!prev || firstOrLoading || this.firstClusterData)) {
      if (this.firstClusterData) this.firstClusterData = false;

      return false;
    }

    if (current && !current.clusterBackup && prev?.clusterBackup) return true;

    const needLoad =
      current?.clusterBackup &&
      current.valid &&
      (prev?.password !== current.password || prev?.login !== current.login || current.valid !== prev?.valid);

    if (this.isEdit && needLoad) this.firstClusterData = isNil(this.firstClusterData);

    return !this.firstClusterData && needLoad;
  }

  private extractCred(data: Partial<SelectHostStepValue>): SelectHostStepValue {
    return {
      server: data?.server,
      login: data?.login,
      password: data?.password,
      valid: !!data?.valid
    };
  }

  private needLoadMachines(changes: SimpleChanges): boolean {
    const curCred = this.extractCred(changes?.selectedHostCredentials?.currentValue);
    const oldCred = this.extractCred(changes?.selectedHostCredentials?.previousValue);
    const isNew = !isEqual(curCred, oldCred) || !isEqual(curCred, this.lastValidCredentials);

    if (isNew && curCred.valid) {
      return !!(curCred.server && curCred.login && curCred.password);
    }

    return false;
  }

  updateForm(value: SelectVirtualMachinesStepValue): void {
    if (this.isOffline) {
      if (value.machines.length && value.type === VirtualMachinesSelectedType.All) value.type = VirtualMachinesSelectedType.Selected;
      if (!value.machinesFullFormat?.length && this.selectedMachines.length) value.machinesFullFormat = this.selectedMachines;
    }

    this.selectedMachinesNames = value.machines;
    this.stepForm.reset(value);

    if (this.isHyperV && !this.isFirstLoad && !this.isOffline) this.checkHyperVAndLoadMachines();
    this.isFirstLoad = false;
  }

  checkHyperVAndLoadMachines(): void {
    this.loadingCheckHyperV = true;
    this.loadInfo.emit({ loading: true, isHyperV: false });
    this.stepService
      .VMFeatureTest(this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          this.hyperVNotInstalled = !value.data;
          if (this.hyperVNotInstalled) this.stepService.showHyperVInfoModal(false);
          else this.loadMachines();
          this.loadInfo.emit({ loading: false, isHyperV: value.data });
          this.stepForm.markAllAsTouched();
          this.stepForm.updateValueAndValidity();
          this.loadingCheckHyperV = false;
        },
        error: () => {
          this.loadInfo.emit({ loading: false, isHyperV: false });
          this.loadingCheckHyperV = false;
        }
      });
  }

  forceValid(stepData: ForceValidType = null): void {
    if (this.isEdit && this.notLoadedSelected) return;

    if (this.virtualizationNotInstalled) return this.stepService.showHyperVInfoModal(false);

    const validSelected = this.selectedMachines.length || this.value?.type !== VirtualMachinesSelectedType.Selected;

    if (!this.isOffline && !this.isRDMode && stepData.isNext && validSelected && !this.stepForm?.errors?.duplicatesExist) {
      this.requestAvailableSockets(stepData.isSave);
    } else {
      this.stepForm.updateValueAndValidity();
      this.stepForm.markAllAsTouched();
    }
  }

  private requestAvailableSockets(isSave: boolean): void {
    const params: ParamsForCheckVMSocketLicenses = this.getSocketRequestParams();
    const isEqualRequest = isEqual(this.availableSocketsRequest.params, params);
    if (this.availableSocketsRequest.inProgress || (this.availableSocketsRequest.finished && isEqualRequest)) {
      return;
    }

    this.loadInfo.emit({ loading: true });

    this.stepService
      .getRemoteCommandData(params, this.mainService.hid, this.needOldAsync)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          const oldAvailableSockets = this.hasAvailableSockets;

          this.availableSocketsRequest = { finished: true, params };
          if (data && data.data) {
            this.updateFlagsFromCheckLicenseRequest(data.data);

            FormsUtil.triggerValidation(this.stepForm);
            this.loadInfo.emit({ loading: false });
            if (this.stepForm.valid && this.hasAvailableSockets) this.nextStep.emit(isSave);

            // Skip mark if previous result was Falsy too
            if (oldAvailableSockets === false && !this.hasAvailableSockets) {
              return;
            }

            if (!this.hasAvailableSockets) {
              this.registerRequiredStep.emit(this.title);
            }
          } else this.loadInfo.emit({ loading: false });
        },
        error: (e) => {
          if (+e?.error?.errorCode === 1003 && e?.error?.detail) {
            this.availableSocketsErrorDetail = e.error.detail;
          }

          this.loadInfo.emit({ loading: false });
          this.availableSocketsRequest = {};
        }
      });

    this.availableSocketsRequest = { inProgress: true };
  }

  updateFlagsFromCheckLicenseRequest(result: CheckLicenseResult): void {
    delete this.availableSocketsErrorDetail;

    const usedSocketsCount = result.usedSocketsCount;
    const availableSocketsCount = result.availableSocketsCount;

    this.hasAvailableSockets = result.isOk && availableSocketsCount >= usedSocketsCount;

    if (!this.hasAvailableSockets && result.message) this.availableSocketsErrorDetail = result.message;
  }

  loadMachines(needLoadMore = false): void {
    if (this.notRDAndNotOffline()) {
      this.machinesLoading = true;
      const params: ParamsForGetVirtualMachinesOrDisksList = this.getMachineListRequestParams(needLoadMore);
      if (!needLoadMore) this.loadInfo.emit({ loading: true });

      return void this.stepService
        .getRemoteCommandData(params, this.mainService.hid, this.needOldAsync)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (data) => {
            this.machinesTotal = data?.data?.totalCount || 0;
            this.machinesLoadedTotal = !needLoadMore ? data.data.items?.length || 0 : this.machinesLoadedTotal + data.data.items?.length;
            if (data?.data?.items) {
              this.setStepDataFromLoadedMachines(this.filterMachinesList(data.data.items), needLoadMore);
            } else {
              this.loadedMachines = needLoadMore ? this.loadedMachines : [];
            }

            this.calcMinusPixelsForCard();

            if (!needLoadMore) this.loadInfo.emit({ loading: false });

            if (this.needLoadMoreMachines(data?.data?.items?.length)) return void setTimeout(() => this.loadMachines(true), 100);

            this.machinesLoading = false;
            this.openPendingSearchModal();
          },
          error: (e) => {
            if (e?.error?.errorCode === ErrorCode.HyperVNotFound) {
              const errorText = e.error?.ErrorMessage || e.error?.detail;
              this.errorService.showToastWithDetails({ error: e.error, modalText: errorText });
            }

            this.loadedMachines = [];
            if (!needLoadMore) this.loadInfo.emit({ loading: false });
            this.machinesLoading = false;
            this.pendingSearchModal = false;
          }
        });
    }

    const newMachines: VirtualMachine[] = this.value.machines.map((name: string, idx: number) => ({
      virtualMachineId: name.replaceAll(' ', '') + idx,
      name: name
    })) as VirtualMachine[];

    this.setStepDataFromLoadedMachines(newMachines);
  }

  filterMachinesList(machines: VirtualMachine[]): VirtualMachine[] {
    return machines.filter((m: VirtualMachine) => m.virtualMachineId !== GuidEmpty);
  }

  setStepDataFromLoadedMachines(machinesArray: VirtualMachine[], needLoadMore = false): void {
    machinesArray.forEach((machine) => (this.loadedMachinesHashTable[machine.virtualMachineId] = machine));
    this.loadedMachines = [...this.loadedMachines, ...this.getMachineWithValidatedName(machinesArray)];
    this.notLoaded = this.machinesTotal - this.machinesLoadedTotal;

    if (!needLoadMore) this.typeChangeHandler(this.value.type);

    if (this.selectedMachinesNames.length) {
      this.selectedMachinesChangesHandler(this.getSelectedMachinesFromArrayMachineName(this.selectedMachinesNames), true);
      if (this.machinesTotal <= this.loadedMachines?.length) this.selectedMachinesNames = [];
    }

    this.applyMachinesFilter();
  }

  private getSocketRequestParams(): ParamsForCheckVMSocketLicenses {
    const type = this.stepForm.get('type').value;

    return {
      agentType: 'backup',
      commandType: this.needNewCheckSocketLicense ? 'CheckVMSocketLicensesById' : 'CheckVMSocketLicenses',
      params: {
        PlanId: this.mainService.planId,
        Type: this.isProxmox ? VirtualMachinesType.Proxmox : this.isVMWare ? VirtualMachinesType.VMWare : VirtualMachinesType.HyperV,
        Server: this.selectedHostCredentials?.server || '',
        UserName: this.selectedHostCredentials?.login || '',
        Password: this.selectedHostCredentials?.password || '',
        VMBackupType: +VirtualMachinesSelectedWithNumbersType[type],
        Machines:
          type === VirtualMachinesSelectedType.All
            ? []
            : this.needNewCheckSocketLicense
              ? this.selectedMachines.map((m) => ({ Id: m.virtualMachineId, Name: m.name }))
              : this.selectedMachines.map((m) => m.virtualMachineId)
      }
    };
  }

  private getMachineListRequestParams(needLoadMore = false): ParamsForGetVirtualMachinesOrDisksList {
    return {
      agentType: 'backup',
      commandType: 'GetVirtualMachineList',
      params: {
        PlanId: this.mainService.planId,
        Type: this.isProxmox ? 'Proxmox' : this.isVMWare ? 'VMWare' : 'HyperV',
        Server: this.isHyperV ? '' : this.selectedHostCredentials?.server || '',
        Login: this.isHyperV ? this.cluster?.login || '' : this.selectedHostCredentials?.login || '',
        Password: this.isHyperV ? this.cluster?.password || '' : this.selectedHostCredentials?.password || '',
        Offset: needLoadMore ? this.machinesLoadedTotal || 0 : 0,
        Limit: 300,
        IsCluster: this.isHyperV && !!this.cluster?.clusterBackup,
        Order: 'DisplayNameAsc',
        SessionId: this.mainService.sessionId || null
      }
    };
  }

  typeChangeHandler(type: VirtualMachinesSelectedType): void {
    if (type === VirtualMachinesSelectedType.Selected) {
      this.selectedMachines = this.cacheSelectedMachines?.length ? this.cacheSelectedMachines : this.selectedMachines;
    } else {
      this.cacheSelectedMachines = this.selectedMachines;
      this.selectedMachines = [];
    }

    this.syncSelectedMachineIds();

    this.stepForm.get('machines').setValue(this.selectedMachines.map((m) => m[this.fieldName]));
    this.stepForm.get('machinesFullFormat').setValue(this.selectedMachines);

    delete this.availableSocketsErrorDetail;

    this.cdr.detectChanges();

    this.notifyMachinesSelected();
  }

  openSearchMachinesModal(searchTooltip: NgbTooltip): void {
    searchTooltip.close();
    (this.document.activeElement as HTMLElement)?.blur?.();

    if (this.notRDAndNotOffline() && this.machinesTotal > this.machinesLoadedTotal) {
      this.pendingSearchModal = true;
      this.loadMachines(true);

      return;
    }

    this.showSearchMachinesModal();
  }

  searchQueryChangeHandler(query: string): void {
    this.searchQuery = query || '';
    this.applyMachinesFilter();
  }

  typeFilterChangeHandler(type: MachineTypeFilter): void {
    this.typeFilter = type || MachineTypeFilter.All;
    this.applyMachinesFilter();
  }

  isMachineSelected(machine: VirtualMachine): boolean {
    return this.selectedMachineIds.has(machine.virtualMachineId);
  }

  machineCheckedChangeHandler(machine: VirtualMachine, checked: boolean): void {
    const machines = checked
      ? [...this.selectedMachines, machine]
      : this.selectedMachines.filter((selected) => selected.virtualMachineId !== machine.virtualMachineId);

    this.selectedMachinesChangesHandler(machines);
  }

  private needLoadMoreMachines(loadedCount: number): boolean {
    if (!loadedCount) return false;

    return !!this.notLoadedSelected || (this.pendingSearchModal && this.machinesTotal > this.machinesLoadedTotal);
  }

  private openPendingSearchModal(): void {
    if (!this.pendingSearchModal) return;

    this.pendingSearchModal = false;
    this.showSearchMachinesModal();
  }

  private showSearchMachinesModal(): void {
    this.searchQuery = '';
    this.typeFilter = MachineTypeFilter.All;
    this.applyMachinesFilter();

    this.modalService.open(this.getSearchModalSettings(), this.searchMachinesModal).then(noop).catch(noop);
  }

  private getSearchModalSettings(): ModalSettings {
    return {
      collapsing: true,
      responsive: true,
      size: MbsSize.xxl,
      header: { title: this.i18nPipe.transform('wizards:searchVirtualMachinesModalTitle', { format: 'title' }) },
      footer: {
        okButton: { show: false },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.close', { format: 'title' }) }
      }
    };
  }

  allMachinesCheckedChangeHandler(checked: boolean): void {
    const filteredIds = new Set(this.filteredMachines.map((machine) => machine.virtualMachineId));
    const machines = checked
      ? [...this.selectedMachines, ...this.filteredMachines.filter((machine) => !this.isMachineSelected(machine))]
      : this.selectedMachines.filter((machine) => !filteredIds.has(machine.virtualMachineId));

    this.selectedMachinesChangesHandler(machines);
  }

  machineRowTrackBy(index: number, row: VirtualMachine[]): string {
    return row.map((machine) => machine.virtualMachineId).join();
  }

  machineRowsScrolledIndexChangeHandler(rowIndex: number): void {
    this.scrolledIndexChangeHandler(rowIndex * this.columnsCount);
  }

  private observeGridColumnsCount(): void {
    this.breakpointObserver
      .observe(MachinesGridColumns.map((breakpoint) => breakpoint.query))
      .pipe(untilDestroyed(this))
      .subscribe((state) => {
        const matched = MachinesGridColumns.find((breakpoint) => state.breakpoints[breakpoint.query]);

        this.columnsCount = matched?.columns || MachinesGridMinColumns;
        this.buildMachineRows();
      });
  }

  private applyMachinesFilter(): void {
    const query = this.searchQuery.trim().toLowerCase();

    this.filteredMachines = this.loadedMachines.filter((machine) => this.matchesQuery(machine, query) && this.matchesTypeFilter(machine));

    this.buildMachineRows();
  }

  private buildMachineRows(): void {
    const rows: VirtualMachine[][] = [];

    for (let index = 0; index < this.filteredMachines.length; index += this.columnsCount) {
      rows.push(this.filteredMachines.slice(index, index + this.columnsCount));
    }

    this.machineRows = rows;

    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    const selectedCount = this.filteredMachines.reduce((count, machine) => count + (this.isMachineSelected(machine) ? 1 : 0), 0);

    this.allFilteredMachinesSelected = !!this.filteredMachines.length && selectedCount === this.filteredMachines.length;
    this.someFilteredMachinesSelected = !this.allFilteredMachinesSelected && !!selectedCount;
  }

  private matchesQuery(machine: VirtualMachine, query: string): boolean {
    return (
      !query ||
      String(machine[this.fieldName] || '')
        .toLowerCase()
        .includes(query)
    );
  }

  private matchesTypeFilter(machine: VirtualMachine): boolean {
    if (!this.isProxmox || this.typeFilter === MachineTypeFilter.All) return true;

    const isContainer = machine.itemType === ItemTypeEnum.Container;

    return this.typeFilter === MachineTypeFilter.Containers ? isContainer : !isContainer;
  }

  private syncSelectedMachineIds(): void {
    this.selectedMachineIds = new Set(this.selectedMachines.map((machine) => machine.virtualMachineId));

    this.updateSelectAllState();
  }

  scrolledIndexChangeHandler(state: number): void {
    if (!state || this.machinesLoading) return;

    if (this.machinesTotal > this.machinesLoadedTotal && state >= this.machinesLoadedTotal) this.loadMachines(true);
  }

  selectedMachinesChangesHandler(selectedMachines: VirtualMachine[], needUpdate = false): void {
    if (needUpdate || !isEqual(this.selectedMachines, selectedMachines)) {
      const excludeNames = this.selectedMachines.filter((n) => !selectedMachines.includes(n)).map((machine) => machine[this.fieldName]);
      const result = selectedMachines.map((machine) => machine[this.fieldName]);

      this.selectedMachines = this.getMachineWithValidatedName(selectedMachines);
      this.syncSelectedMachineIds();
      this.selectedMachinesNames = this.selectedMachinesNames.filter((n) => !excludeNames.includes(n));

      const existNotLoaded = this.selectedMachinesNames.reduce((count, name) => {
        if (!result.includes(name)) count++;

        return count;
      }, 0);
      this.notLoadedSelected = this.machinesTotal > this.machinesLoadedTotal && existNotLoaded;

      if (existNotLoaded && !this.notLoadedSelected) {
        this.selectedMachinesNames = this.selectedMachinesNames.filter((n) => result.includes(n));
      }

      this.stepForm.get('machines').setValue(result);
      this.stepForm.get('machinesFullFormat').setValue(this.selectedMachines);

      delete this.availableSocketsErrorDetail;
    }

    this.notifyMachinesSelected();
  }

  getMachineWithValidatedName(machines: VirtualMachine[]): VirtualMachine[] {
    this.loadedMachines.forEach((machine) => (machine.invalidName = false));
    const keys = Object.keys(this.loadedMachinesHashTable);

    return machines.map((machine) => {
      machine.invalidName = keys.some(
        (key) =>
          key !== machine.virtualMachineId &&
          machines.includes(this.loadedMachinesHashTable[key]) &&
          this.loadedMachinesHashTable[key][this.fieldName] === machine[this.fieldName]
      );

      return machine;
    });
  }

  getMachineWithValidatedDisks(errorIds: string[], all = false): void {
    const selectedType = this.stepForm.get('type').value === VirtualMachinesSelectedType.Selected;

    this.selectedMachines = this.selectedMachines.map((machine) => {
      machine.invalidDisks = selectedType && (all || errorIds.some((id) => id === machine.virtualMachineId));

      return machine;
    });
  }

  getSelectedMachinesFromArrayMachineName(machines: string[]): VirtualMachine[] {
    const selected: VirtualMachine[] = [];
    const machinesCount = machines.length;

    this.loadedMachines.forEach((machine) => {
      if (selected.length < machinesCount && machines.includes(machine[this.fieldName])) {
        selected.push(machine);
      }
    });

    return selected;
  }

  private notifyMachinesSelected(): void {
    if (this.stepForm.get('type').value === VirtualMachinesSelectedType.Selected) {
      const selectedList = this.stepForm.get('machines').value;

      return void this.machinesSelected.emit(this.loadedMachines.filter((machine) => selectedList.includes(machine[this.fieldName])));
    }

    this.machinesSelected.emit(this.loadedMachines);
  }

  private calcMinusPixelsForCard(): void {
    let pixels = 180;

    if (this.isOffline) pixels += 91;
    if (this.isHyperV && this.hyperVNotInstalled) pixels += 51;

    if (this.notLoaded && (!this.isEdit || !this.notLoadedSelected)) pixels += 23;
    if (this.isEdit && this.notLoaded && this.notLoadedSelected) pixels += 67;
    if (this.stepForm?.errors?.duplicatesExist) pixels += 71;
    if (!this.notLoadedSelected && this.mainService?.machinesDisksStatuses$?.value?.loadingDisks) pixels += 37;
    if (this.availableSocketsErrorDetail) pixels += 44;
    if (this.isOffline) pixels += 35;

    this.minusPixels = pixels;
  }
}
